// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {connectionToNodes} from '@wandb/weave/core/ops/domain/util';
import {useNodeValue, useRefreshAllNodes} from '@wandb/weave/react';
import React, {useContext, useEffect, useMemo, useState} from 'react';

import {envIsPublicCloud} from '../../../../../config';
import {
  useEntityTagsQuery,
  useRegistryProjectsWithTypeQuery,
  WorkflowsAdminType,
} from '../../../../../generated/graphql';
import * as Generated from '../../../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useTeamMember} from '../../../../../state/graphql/teamMembers';
import {useViewer} from '../../../../../state/viewer/hooks';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../../util/errors';
import {capitalizeFirstLetter} from '../../../../../util/string';
import {
  artifactsNodeToCollectionInfo,
  CollectionInfo,
  useRegistryProjectArtifactPortfoliosNode,
} from '../../../../ModelRegistry/utils';
import {AccountSelectorContext} from '../../../../Search/SearchNav/AccountSelectorContextProvider';
import {mapGQLTags} from '../../../../TagAssignment';
import {useIsGlobalRegistryEnabled} from '../../../common/hooks';
import {checkRegistryProject} from '../../../common/utils';
import {
  getIsViewerRegistryAdmin,
  getRegistryMembers,
  getRegistryMembersMap,
  getRegistryTeamMembers,
} from '../../../RegistrySettings/RegistryMembersTable/utils';
import {Artifact, Collection} from '../CreateRegisteredCollectionDrawerInner';
import * as S from '../CreateRegisteredCollectionDrawerInner.styles';
import {
  useAddTagsToCollection,
  useCreateRegisteredCollection,
  useDefaultProjectExists,
  useLinkArtifactVersion,
} from '../hooks';
import {
  DrawerPageCollectionConfiguration,
  GenericOptionType,
} from './DrawerPageCollectionConfiguration';
import {DrawerPageLinkVersion} from './DrawerPageLinkVersion';

enum Steps {
  SELECT_COLLECTION,
  ADD_ALIASES,
}

type LinkArtifactDrawerInnerProps = {
  onClose: () => void;

  artifactTypeName: string;
  defaultProjectName: string;
  artifact: Artifact;
  afterLink: (collection: CollectionInfo) => void;
};

export const LinkArtifactDrawerInner = ({
  artifactTypeName,
  defaultProjectName,
  onClose,
  afterLink,
  artifact,
}: LinkArtifactDrawerInnerProps) => {
  // Registries feature flag
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? selectedAccount?.name
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {enableGlobalRegistry, enableGlobalRegistryLoading} =
    useIsGlobalRegistryEnabled(artifact.entityName);

  const [selectedRegistry, setSelectedRegistry] = useState<GenericOptionType>();

  const {registryOptions, orgEntityName, registryOptionsLoading} =
    useRegistryOptions(
      artifactTypeName,
      orgName,
      enableGlobalRegistry,
      artifact
    );

  // general hooks
  const refreshNodes = useRefreshAllNodes();

  // Common state variables
  const [step, setStep] = useState(Steps.SELECT_COLLECTION);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [useExistingCollection, setUseExistingCollection] = useState(true);

  // Collection config
  const [collection, setCollection] = useState<Collection>({
    name: '',
    id: '',
    description: '',
    entityName: enableGlobalRegistry ? orgEntityName : artifact.entityName,
    projectName: enableGlobalRegistry ? '' : defaultProjectName,
    tags: [],
    numVersions: 0,
    artifactTypeName,
  });
  const [isInvalidCollectionName, setIsInvalidCollectionName] = useState(false);

  // Create collection
  const defaultProjectDoesNotExist = useDefaultProjectExists(
    defaultProjectName,
    artifact.entityName ?? ''
  );
  const {
    createRegisteredCollection,
    loading: loadingCreateRegisteredCollection,
  } = useCreateRegisteredCollection(
    collection.entityName,
    collection.projectName
  );

  // Tags
  const {data: entityData} = useEntityTagsQuery({
    variables: {
      entityName: artifact.entityName ?? '',
    },
    skip: !artifact.entityName && useExistingCollection,
  });
  const entity = entityData?.entity;
  const allEntityTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    const listRawTags = mapGQLTags(rawTags);
    return listRawTags.map(rawTag => rawTag.name);
  }, [entity]);
  const {createTagAssignment, loading: loadingCreateTagAssignment} =
    useAddTagsToCollection();

  // Link version
  const {linkArtifactVersion, loading: loadingLinkArtifactVersion} =
    useLinkArtifactVersion();

  // Aliases (and Protected aliases)
  const [aliasList, setAliasList] = useState<string[]>([]);
  const [isInvalidAliasList, setIsInvalidAliasList] = useState(false);
  const viewer = useViewer();
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: artifact.entityName,
  });

  const legacyProtectedAliasesQuery = Generated.useEntityProtectedAliasesQuery({
    variables: {
      entityName: artifact.entityName,
      workflowsAdminType: WorkflowsAdminType.Model,
    },
  });

  const linkingToRegistryProject = useMemo(() => {
    const {isRegistryProject} = checkRegistryProject(collection.projectName);
    return isRegistryProject;
  }, [collection.projectName]);

  const registryProtectedAliasesQuery =
    Generated.useProjectProtectedAliasesQuery({
      skip:
        collection.projectName == null ||
        collection.entityName == null ||
        !linkingToRegistryProject,
      variables: {
        entityName: collection.entityName,
        projectName: collection.projectName,
      },
    });

  const registryProjectMembersQuery = Generated.useRegistryProjectMembersQuery({
    skip:
      collection.projectName == null ||
      collection.entityName == null ||
      !linkingToRegistryProject,
    variables: {
      entityName: collection.entityName,
      projectName: collection.projectName,
    },
  });

  const protectedAliases = React.useMemo(() => {
    const legacyProtectedAliases =
      legacyProtectedAliasesQuery.data?.entity?.protectedAliases ?? [];
    const registryProtectedAliases =
      registryProtectedAliasesQuery.data?.project?.protectedArtifactAliases.map(
        alias => alias.alias
      ) ?? [];
    return linkingToRegistryProject
      ? registryProtectedAliases
      : legacyProtectedAliases;
  }, [
    linkingToRegistryProject,
    legacyProtectedAliasesQuery.data,
    registryProtectedAliasesQuery.data,
  ]);

  const workflowsAdmins = React.useMemo(() => {
    return legacyProtectedAliasesQuery.data?.entity?.workflowsAdmins ?? [];
  }, [legacyProtectedAliasesQuery.data]);

  const isViewerRegistryAdmin = useMemo(() => {
    const registryTeamMembers = getRegistryTeamMembers(
      registryProjectMembersQuery.data?.project?.teamMembers ?? []
    );
    // HACK: we don't need to get the members org role to determine if they are
    // a registry admin, so we just pass in an empty array for the organization
    // members
    const registryMembers = getRegistryMembers(
      [],
      registryProjectMembersQuery.data?.project?.members ?? []
    );
    return getIsViewerRegistryAdmin(
      getRegistryMembersMap(registryMembers),
      getRegistryMembersMap(registryTeamMembers),
      viewer
    );
  }, [
    registryProjectMembersQuery.data?.project?.members,
    registryProjectMembersQuery.data?.project?.teamMembers,
    viewer,
  ]);

  const username = viewer?.username ?? '';
  const isRegistryAdmin = linkingToRegistryProject
    ? isViewerRegistryAdmin
    : workflowsAdmins.some(admin => admin.username === username) ||
      viewerIsTeamAdmin;

  const portfoliosNode = useRegistryProjectArtifactPortfoliosNode(
    artifactTypeName,
    collection.entityName,
    collection.projectName
  );

  const collectionNamesDict = useNodeValue(
    useMemo(() => {
      return artifactsNodeToCollectionInfo(portfoliosNode as any);
    }, [portfoliosNode])
  );
  const existingCollections: CollectionInfo[] = useMemo(
    () => collectionNamesDict.result?.artifactCollections ?? [],
    [collectionNamesDict]
  );

  // Enable or disable button to go to the next page based on user input
  useEffect(() => {
    if (
      collection.name === '' ||
      collection.entityName === '' ||
      defaultProjectDoesNotExist.loading
    ) {
      setNextButtonDisabled(true);
    } else if (step === Steps.ADD_ALIASES && isInvalidAliasList) {
      setNextButtonDisabled(true);
    } else if (useExistingCollection && collection.id === '') {
      setNextButtonDisabled(true);
    } else if (!useExistingCollection) {
      if (
        existingCollections
          ?.filter(
            c =>
              c.entityName === collection.entityName &&
              c.projectName === collection.projectName
          )
          .some(c => c?.collectionName === collection.name)
      ) {
        setNextButtonDisabled(true);
      } else if (isInvalidCollectionName) {
        setNextButtonDisabled(true);
      } else {
        setNextButtonDisabled(false);
      }
    } else {
      setNextButtonDisabled(false);
    }
  }, [
    step,
    collection.entityName,
    collection.name,
    collection.id,
    collection.projectName,
    existingCollections,
    useExistingCollection,
    defaultProjectDoesNotExist.loading,
    isInvalidAliasList,
    isInvalidCollectionName,
    setNextButtonDisabled,
  ]);

  useEffect(() => {
    if (!enableGlobalRegistryLoading && orgEntityName !== '') {
      setCollection(c => ({
        ...c,
        entityName: orgEntityName,
      }));
    }
  }, [enableGlobalRegistryLoading, orgEntityName]);

  if (
    collectionNamesDict.loading ||
    enableGlobalRegistryLoading ||
    registryOptionsLoading
  ) {
    return null;
  }
  return (
    <S.RegisteredCollectionDrawer>
      <S.CenteredFlex>
        <S.Header>Link to registry</S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider />
      <S.RegisteredCollectionDrawerMain>
        {step === Steps.SELECT_COLLECTION && (
          <DrawerPageCollectionConfiguration
            collection={collection}
            entityTagsList={allEntityTags}
            showModelLimitReached={false}
            useExistingCollection={useExistingCollection}
            setUseExistingCollection={setUseExistingCollection}
            collectionsList={existingCollections}
            isInvalidCollectionName={isInvalidCollectionName}
            setIsInvalidCollectionName={setIsInvalidCollectionName}
            setCollection={setCollection}
            selectedRegistry={selectedRegistry}
            onRegistrySelected={(option: GenericOptionType | undefined) => {
              setSelectedRegistry(option);
              if (option != null) {
                const newProjectName = option.value;
                setCollection({
                  ...collection,
                  entityName:
                    enableGlobalRegistry &&
                    newProjectName !== OLD_MODEL_REGISTRY_PROJECT_NAME
                      ? orgEntityName
                      : artifact.entityName,
                  projectName: newProjectName,
                });
              }
            }}
            registryOptions={registryOptions}
            enableGlobalRegistry={enableGlobalRegistry}
          />
        )}
        {step === Steps.ADD_ALIASES && (
          <DrawerPageLinkVersion
            collection={collection}
            artifact={artifact}
            aliasList={aliasList}
            setAliasList={setAliasList}
            isInvalidAliasList={isInvalidAliasList}
            setIsInvalidAliasList={setIsInvalidAliasList}
            protectedAliases={protectedAliases}
            viewerIsRegistryAdmin={isRegistryAdmin}
          />
        )}
      </S.RegisteredCollectionDrawerMain>
      <S.Divider />
      <S.Footer>
        {step === Steps.SELECT_COLLECTION ? (
          <S.FooterLeftContent>
            <S.HelpMessage>
              You provide version details in the next step.
            </S.HelpMessage>
          </S.FooterLeftContent>
        ) : (
          <S.FooterLeftContent>
            <Button
              className="create-button"
              variant="primary"
              size="large"
              onClick={() => {
                setStep(step - 1);
              }}>
              Back
            </Button>
          </S.FooterLeftContent>
        )}
        <S.FooterRightContent>
          {step !== Steps.SELECT_COLLECTION && (
            <Button
              data-test="link-button"
              variant="primary"
              size="large"
              onClick={async () => {
                let linkCollectionID = collection.id;
                let linkArtifactResult;
                if (!useExistingCollection) {
                  const createCollectionResult =
                    await createRegisteredCollection(
                      collection.name,
                      collection.description,
                      collection.entityName,
                      collection.projectName,
                      collection.artifactTypeName,
                      defaultProjectDoesNotExist.data
                    );
                  if (createCollectionResult && collection.tags.length > 0) {
                    await createTagAssignment(
                      collection.tags,
                      collection.entityName,
                      collection.projectName,
                      collection.name
                    );
                  }
                  linkCollectionID = createCollectionResult?.id ?? '';
                }
                if (
                  step === Steps.ADD_ALIASES &&
                  artifact.id &&
                  linkCollectionID
                ) {
                  linkArtifactResult = await linkArtifactVersion(
                    artifact.id,
                    linkCollectionID,
                    collection.name,
                    aliasList
                  );
                }
                await refreshNodes();
                onClose();
                let chosenCollection = existingCollections.find(
                  c => c.collectionId === linkCollectionID
                );
                if (!useExistingCollection && linkCollectionID) {
                  chosenCollection = {
                    entityName: collection.entityName,
                    projectName: collection.projectName,
                    artifactTypeName,
                    collectionName: collection.name,
                    collectionId: linkCollectionID,
                  };
                }
                if (chosenCollection && linkArtifactResult != null) {
                  afterLink(chosenCollection);
                }
              }}
              disabled={
                nextButtonDisabled ||
                loadingCreateRegisteredCollection ||
                loadingCreateTagAssignment ||
                loadingLinkArtifactVersion
              }>
              Link to registry
            </Button>
          )}
          {step !== Steps.ADD_ALIASES && (
            <Button
              data-test="next-button"
              variant="primary"
              size="large"
              endIcon="forward-next"
              onClick={() => {
                setStep(step + 1);
              }}
              disabled={nextButtonDisabled}>
              Next step
            </Button>
          )}
        </S.FooterRightContent>
      </S.Footer>
    </S.RegisteredCollectionDrawer>
  );
};

/**
 * This hook is used to populate the registry options dropdown. It looks for
 * both new global registry projects and the legacy model registry project.
 */
const useRegistryOptions = (
  artifactTypeName: string,
  orgName: string | undefined,
  enableGlobalRegistry: boolean,
  artifact: Artifact
) => {
  // Get the new global registry projects
  const {data: orgRegistryProjects, loading: orgRegistryProjectsLoading} =
    useRegistryProjectsWithTypeQuery({
      variables: {orgName: orgName ?? '', artifactTypeName},
      context: propagateErrorsContext(),
      skip: orgName == null || !enableGlobalRegistry,
    });
  const orgEntityName =
    orgRegistryProjects?.organization?.orgEntity?.name ?? artifact.entityName;

  // Get the legacy model registry project. Note that it may not exist.
  const {
    data: legacyModelRegistryProject,
    loading: legacyModelRegistryProjectLoading,
  } = Generated.useLegacyRegistryProjectsQuery({
    variables: {entityName: artifact.entityName},
    context: propagateErrorsContext(),
    skip:
      // The only type of legacy registry we have is for models
      artifactTypeName !== 'model' ||
      // when global registry isn't turned on, we don't show a list of registries, so
      // no need for this query
      !enableGlobalRegistry,
  });
  const legacyModelProject = legacyModelRegistryProject?.projects?.edges?.find(
    edge => edge?.node?.name === OLD_MODEL_REGISTRY_PROJECT_NAME
  )?.node;

  const registryOptions = useMemo(() => {
    const newRegistryOptions: GenericOptionType[] = [];

    for (const edge of orgRegistryProjects?.organization?.orgEntity?.projects
      ?.edges ?? []) {
      const registryProject = edge?.node;
      if (registryProject != null) {
        const allowsArtifactType =
          registryProject.artifactType != null ||
          registryProject.allowAllArtifactTypesInRegistry;
        const {registryName} = checkRegistryProject(registryProject.name);
        if (registryName != null && allowsArtifactType) {
          newRegistryOptions.push({
            value: registryProject.name,
            label: capitalizeFirstLetter(registryName),
          });
        }
      }
    }
    const legacyModelProjectArtifactCollectionsCount =
      legacyModelProject?.totalArtifactCollections ?? 0;

    if (
      artifactTypeName === 'model' &&
      // PM decision: we will not show the legacy model registry entry
      // if the user hasn't actually added any collections to it. We don't
      // want to encourage usage of the legacy model registry since we are
      // deprecating it.
      legacyModelProjectArtifactCollectionsCount > 0
    ) {
      newRegistryOptions.push({
        value: OLD_MODEL_REGISTRY_PROJECT_NAME,
        label: 'Legacy model registry',
      });
    }
    return newRegistryOptions;
  }, [orgRegistryProjects, artifactTypeName, legacyModelProject]);
  return {
    registryOptions,
    orgEntityName,
    registryOptionsLoading:
      orgRegistryProjectsLoading || legacyModelRegistryProjectLoading,
  };
};
